import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { PublicKey } from "@solana/web3.js";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { AssetConfig } from "../../../config/assets";
import { useBalance } from "../../../hooks/useBalance";
import { useEnqueueWithdrawal } from "../../../hooks/useEnqueueWithdrawal";
import { useBurnWithdrawal } from "../../../hooks/useBurnWithdrawal";
import { useExchangeRate } from "../../../hooks/useExchangeRate";
import { useErrorToast, useSuccessToast } from "../../../helpers/toast";
import { solAmountFormatter } from "../../../helpers/formatter";
import { getCurrentEpochInfo } from "../../../helpers/epoch";
import { getEstimatedWithdrawalWait } from "../../../helpers/getEstimatedWithdrawalWait";
import { chunkTicketsForBatchClaim } from "../../../helpers/batchClaim";
import { WithdrawalTicket } from "../../../helpers/dataStructures";
import { useStakeWithdrawalTickets } from "../StakeWithdrawalTicketsContext";
import { WithdrawalTicketsSection } from "../WithdrawalTicketsSection";
import { StakeFormDetailItem, StakeFormProps, TokenAddon } from "../StakeForm";

interface UseUnstakeStandardOptions {
  config: AssetConfig;
  resetKey: string;
  vrtApy: number | null;
}

export const useUnstakeStandard = ({ config, resetKey, vrtApy }: UseUnstakeStandardOptions) => {
  const { connection } = useConnection();
  const { publicKey, wallet } = useWallet();

  const { balance, fetchBalance } = useBalance();
  const { refresh: refreshWithdrawalTickets } = useStakeWithdrawalTickets();
  const { errorToast } = useErrorToast();
  const { successToast } = useSuccessToast();
  const { enqueueWithdrawal, error: enqueueWithdrawalError } = useEnqueueWithdrawal();
  const { burnWithdrawal, burnWithdrawalBatch, error: burnWithdrawalError } = useBurnWithdrawal();
  const { rate: vrtRate, fetchRate: fetchVrtRate } = useExchangeRate();

  const [currentEpoch, setCurrentEpoch] = useState(0);
  const [claimingTicketId, setClaimingTicketId] = useState<string | null>(null);
  const [isBatchClaiming, setIsBatchClaiming] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [estimatedWait, setEstimatedWait] = useState<string | null>(null);

  const unstakeMintLabel =
    config.id === "sol" ? "JitoSOL" : config.underlyingLabel;

  const formik = useFormik({
    initialValues: { amount: "", receivedAmount: "" },
    onSubmit: async (values) => {
      try {
        const signature = await enqueueWithdrawal(
          config.vrtMintAddress,
          config.vaultAddress,
          values.amount
        );
        if (signature) {
          successToast("Your withdrawal request was successful!");
          reload();
        }
      } catch (err) {
        errorToast(`${err}`);
      }
    },
  });

  const burnFormik = useFormik({
    initialValues: { claimTicket: null as WithdrawalTicket | null },
    onSubmit: async (values) => {
      if (!values.claimTicket) return;
      setClaimingTicketId(values.claimTicket.base.toString());
      try {
        const signature = await burnWithdrawal(
          config.vrtMintAddress,
          config.mintAddress,
          config.vaultAddress,
          values.claimTicket
        );
        if (signature) {
          successToast("Your withdrawal was successful!");
          reload();
        }
      } catch (err) {
        errorToast(`${err}`);
      } finally {
        setClaimingTicketId(null);
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numberValue = Number(inputValue);
    const burnFee = 0.998;
    const outputValue = (numberValue * burnFee) / (vrtRate ?? 1);
    formik.setValues({
      amount: inputValue,
      receivedAmount: solAmountFormatter.format(Number(outputValue)),
    });
  };

  const handleMaxClick = () => {
    const maxOutput = (Number(balance ?? "0") / (vrtRate ?? 1)) * 0.998;
    formik.setValues({
      amount: balance || "0",
      receivedAmount: solAmountFormatter.format(maxOutput).replace(/,/g, ""),
    });
  };

  const reload = () => {
    formik.resetForm();
    fetchBalance(new PublicKey(config.vrtMintAddress), publicKey);
    refreshWithdrawalTickets();
    fetchVrtRate(config.vaultAddress);
  };

  const handleClaimClick = (ticket: WithdrawalTicket) => {
    burnFormik.setValues({ claimTicket: ticket });
    burnFormik.submitForm();
  };

  const handleClaimAll = async (readyTickets: WithdrawalTicket[]) => {
    if (readyTickets.length === 0) return;

    setIsBatchClaiming(true);
    try {
      const chunks = chunkTicketsForBatchClaim(readyTickets);
      let claimed = 0;

      for (const chunk of chunks) {
        const signature = await burnWithdrawalBatch(
          config.vrtMintAddress,
          config.mintAddress,
          config.vaultAddress,
          chunk
        );
        if (!signature) {
          if (claimed > 0) {
            successToast(`Claimed ${claimed} of ${readyTickets.length} requests.`);
            reload();
          }
          return;
        }
        claimed += chunk.length;
      }

      successToast(
        readyTickets.length === 1
          ? "Your withdrawal was successful!"
          : `Claimed ${readyTickets.length} unstaking requests.`
      );
      reload();
    } catch (err) {
      errorToast(`${err}`);
    } finally {
      setIsBatchClaiming(false);
    }
  };

  useEffect(() => {
    if (enqueueWithdrawalError) {
      errorToast(enqueueWithdrawalError);
      formik.setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueWithdrawalError]);

  useEffect(() => {
    if (burnWithdrawalError) {
      errorToast(burnWithdrawalError);
      formik.setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [burnWithdrawalError]);

  useEffect(() => {
    getCurrentEpochInfo(connection).then((epochInfo) => setCurrentEpoch(epochInfo.epoch));
  }, [connection, publicKey]);

  useEffect(() => {
    getEstimatedWithdrawalWait(connection).then(setEstimatedWait);
  }, [connection, resetKey]);

  useEffect(() => {
    formik.resetForm();
    fetchBalance(new PublicKey(config.vrtMintAddress), publicKey);
    fetchVrtRate(config.vaultAddress);
    // Tickets are loaded by StakeWithdrawalTicketsProvider (vault-level); skip refetch on mode-only switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, publicKey, wallet]);

  useEffect(() => {
    setFormDisabled(
      Number(formik.values.amount) > Number(balance?.toString() || "0") ||
        formik.values.amount === ""
    );
  }, [formik.values.amount, balance]);

  const unstakeDetails: StakeFormDetailItem[] = [
    {
      label: "Rate",
      value: `1 ${config.vrtName} = ${(1 / (vrtRate ?? 1))?.toFixed(5) ?? 1} ${unstakeMintLabel}`,
    },
  ];
  if (vrtApy != null) {
    unstakeDetails.push({
      label: "APY impact",
      value: `−${(vrtApy * 100).toFixed(2)}%`,
    });
  }
  if (estimatedWait) {
    unstakeDetails.push({
      label: "Claim ETA",
      value: estimatedWait,
    });
  }

  const stakeFormProps: StakeFormProps = {
    displayedBalance: `${balance} ${config.vrtName}`,
    amount: formik.values.amount,
    receivedAmount: formik.values.receivedAmount,
    onAmountChange: handleInputChange,
    onMax: handleMaxClick,
    inputLabel: "Unstake",
    outputLabel: "Receive",
    inputTokenSelector: <TokenAddon image={config.vrtImage} symbol={config.vrtName} />,
    outputTokenSelector: <TokenAddon image={config.mintImage} symbol={unstakeMintLabel} />,
    receiveHint: "No slippage · 0.2% fee",
    details: unstakeDetails,
    amountInputType: "number",
    submitLabel: "Enqueue Withdrawal",
    formDisabled,
    vaultPaused: config.vaultPaused,
    isSubmitting: formik.isSubmitting,
    onSubmit: formik.handleSubmit,
  };

  const belowForm = publicKey ? (
    <WithdrawalTicketsSection
      currentEpoch={currentEpoch}
      onClaimClick={handleClaimClick}
      onClaimAll={handleClaimAll}
      claimingTicketId={claimingTicketId}
      isBatchClaiming={isBatchClaiming}
      currency={config.vrtName}
      image={config.vrtImage}
    />
  ) : null;

  return { stakeFormProps, belowForm };
};
